import {
  AppContextProvider,
  GlobalStyles,
  Modal,
  ThemeContextProvider,
} from '@evrekadev/evreka-ui-components'
import '@evrekadev/evreka-ui-components/dist/common.css'
import * as Sentry from '@sentry/react'
import { Flex, Loading } from 'components'
import moment from 'moment'
import React, { Suspense, useEffect } from 'react'
import { createRoot } from 'react-dom/client'
import { Helmet } from 'react-helmet'
import { Provider } from 'react-redux'
import Routing from 'routes/Routing'
import store from 'services/store'
import { StyleSheetManager } from 'styled-components'
import stylisRTLPlugin from 'stylis-plugin-rtl'
import useDeviceDetection from 'utils/useDeviceDetections'
import { useRtl } from 'utils/useRtl'
import { useTranslation } from 'utils/useTranslation'
import 'utils/wdyr'
import reportWebVitals from './utils/reportWebVitals'
import { PermissionProvider } from 'services/slices/PermissionProvider'
import { DummyClientsMockDataProvider } from 'providers/DummyClientsMockDataProvider'
import { MobileGlobalStyles } from 'styles/MobileGlobalStyles'

/*
We're overriding en-US as en-GB
https://github.com/moment/moment/blob/e96809208c9d1b1bbe22d605e76985770024de42/dist/locale/en-gb.js#L17
*/
moment.updateLocale('en', {
  longDateFormat: {
    LT: 'HH:mm',
    LTS: 'HH:mm:ss',
    L: 'DD/MM/YYYY',
    LL: 'D MMMM YYYY',
    LLL: 'D MMMM YYYY HH:mm',
    LLLL: 'dddd, D MMMM YYYY HH:mm',
  },
})

const EVREKA360_IOS_APP_URL = 'https://apps.apple.com/tr/app/evreka360/id1582192417'
const EVREKA360_ANDROID_APP_URL =
  'https://play.google.com/store/apps/details?id=co.evreka.smartwaste.evreka360'

if (!import.meta.env.MODE || import.meta.env.MODE === 'development') {
} else {
  const replayRequired = !window.envVariables.REACT_APP_BASE_URL.includes('360-int')

  Sentry.init({
    ignoreErrors: [
      'ResizeObserver loop limit exceeded',
      'UnhandledRejection',
      '_controlCorners',
      'site_id',
      'CustomEvent',
      '_leaflet_pos',
      'postMessage',
    ],
    dsn: window.envVariables.REACT_APP_SENTRY_DSN,
    integrations: replayRequired ? [Sentry.replayIntegration()] : undefined,
    environment: window.envVariables.REACT_APP_SENTRY_ENV,
    release: 'dashboard-next@' + import.meta.env.VITE_REACT_APP_VERSION,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
  })
}

function AppWithStyledWrapper() {
  const isRtl = useRtl()
  return (
    <StyleSheetManager
      key={isRtl ? 'rtl' : 'ltr'}
      stylisPlugins={isRtl ? [stylisRTLPlugin] : undefined}
    >
      <PermissionProvider>
        <Routing />
      </PermissionProvider>
    </StyleSheetManager>
  )
}

function AppWithStore() {
  const { i18n, t } = useTranslation()
  return (
    <div>
      <Helmet>
        <title>{t('dashboard.title')}</title>
        <html lang={i18n.language} />
      </Helmet>
      <GlobalStyles />
      <MobileGlobalStyles />
      <Provider store={store}>
        <DummyClientsMockDataProvider>
          <AppWithStyledWrapper />
        </DummyClientsMockDataProvider>
      </Provider>
    </div>
  )
}

const suspenseStyle: React.CSSProperties = { height: '100vh' }

function App() {
  const { i18n } = useTranslation()
  const { t } = useTranslation('evreka_app_modal')

  const { dismiss, showModal, close } = useDeviceDetection()

  useEffect(() => {
    const reloadOnPreloadError = (event: VitePreloadErrorEvent) => {
      event.preventDefault()
      window.location.reload()
    }
    window.addEventListener('vite:preloadError', reloadOnPreloadError)

    return () => {
      window.removeEventListener('vite:preloadError', reloadOnPreloadError)
    }
  }, [])

  return (
    <AppContextProvider currentLanguage={i18n.language}>
      <ThemeContextProvider>
        <Suspense
          fallback={
            <Flex style={suspenseStyle} center="zero" middle="zero">
              <Loading />
            </Flex>
          }
        >
          <Modal
            customCancelText={t('dismiss_button')}
            onRequestClose={close}
            onCancel={dismiss}
            customSubmitText={t('submit_button')}
            isOpen={showModal}
            heading={t('title')}
            onSubmit={() =>
              window.open(
                localStorage.getItem('device') === 'ios'
                  ? EVREKA360_IOS_APP_URL
                  : EVREKA360_ANDROID_APP_URL,
              )
            }
          >
            {t('content')}
          </Modal>

          <AppWithStore />
        </Suspense>
      </ThemeContextProvider>
    </AppContextProvider>
  )
}

const container = document.getElementById('root')
const root = createRoot(container!)

root.render(<App />)

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals()
